<template>
  <Form as="div" ref="Form">
    <div v-cloak>
      <page-spinner :spinner-url-list="spinnerUrlList" />

      <div v-if="totalProposals > 0" id="checkout" data-page-type="flow">
        <div class="hide-for-large">
          <!-- Sitecore Placeholder "phRightDesktopTopMobile" -->
          <slot name="topmobile" />
        </div>

        <two-columns-page-layout id="mainView">
          <template #left>
            <keep-alive>
              <transition name="fade" mode="out-in">
                <component
                  :is="currentTemplate"
                  ref="proposalView"
                  :key="index"
                  :external-vue-model="currentModel"
                  :proposal-model="proposalModel"
                  :ext-vue-model="filteredViewModel"
                  :name="'main-view'"
                  @vue-model-updated="updateCurrentModel"
                  @go-back="back"
                />
              </transition>
            </keep-alive>

            <div v-if="currentTemplate !== 'checkout-summary'" v-cloak class="row">
              <div class="columns">
                <button
                  class="button secondary"
                  :class="{ hide: index === 0 }"
                  @click="back"
                  v-html="filteredViewModel.Content.LabelBack"
                />
                <a
                  class="button secondary"
                  :href="filteredViewModel.Content.BasketLinkModel.Url"
                  :class="{ hide: index !== 0 }"
                  v-html="filteredViewModel.Content.LabelBack"
                />
              </div>
              <div class="columns" @disable-next-button="disableNextButton">
                <button
                  class="button"
                  :disabled="disableNext"
                  @click="next"
                  v-html="filteredViewModel.Content.ButtonLabelNext"
                />
              </div>
            </div>
          </template>

          <template #right>
            <basket-price-list
              data-test-basket-price-list
              :headline="'Kurv'"
              :total-price-label="filteredViewModel.Content.LabelTotalPrice"
              :total-fees-label="filteredViewModel.Content.LabelFees"
              :per-year-label="filteredViewModel.Content.LabelPrYear"
              :proposals="proposalPriceModels"
              :total-price="totalPrice"
              :total-fees="totalFees"
            />

            <div class="checkout__campaign-box--desktop">
              <!-- Sitecore Placeholder "phRightDesktopTopMobile" -->
              <slot name="rightcolumn" />
            </div>
          </template>
        </two-columns-page-layout>
      </div>
    </div>
  </Form>
</template>

<script>
import axios from 'axios';
import CheckoutAccident from './Accident.vue';
import CheckoutCar from './Car.vue';
import CheckoutCaravan from './Caravan.vue';
import CheckoutContent from './Content.vue';
import CheckoutConstruction from './Construction.vue';
import CheckoutHealth from './Health.vue';
import CheckoutHouse from './House.vue';
import CheckoutMoped from './Moped.vue';
import CheckoutMobility from './Mobility.vue';
import CheckoutMotorcycle from './Motorcycle.vue';
import CheckoutPet from './Pet.vue';
import CheckoutSalaryProtection from './SalaryProtection.vue';
import CheckoutSummary from './Summary.vue';
import CheckoutVacationHome from './VacationHome.vue';
import CheckoutVeteran from './Veteran.vue';
import ProgressBar from '@/components/UI/ProgressBar.vue';
import ValidatePropsMixin from '../ValidatePropsMixin.vue';
import VueSelect from '@/components/UI/VueSelect.vue';
import '@/common/FlowBasket.vue';
import ProposalHeaderView from './Shared/ProposalHeaderView.vue';
import CheckoutProgressBarView from './Shared/CheckoutProgressBarView.vue';
import CampaignBox from '@/components/Feature/CampaignBox/CampaignBoxMain.vue';
import BasketPriceList from '@/components/Feature/Basket/BasketPriceList.vue';
import TwoColumnsPageLayout from '@/components/UI/TwoColumnsPageLayout.vue';
import Utility from '@/common/UtilityFunctions.js';
import DatePicker from '@/components/UI/Pickadate.vue';
import { mapState } from 'vuex';
import { forEach } from 'lodash';
import PageSpinner from '@/components/UI/PageSpinner.vue';
import { cloneDeep } from 'lodash';

export default {
  name: 'CheckOut',
  components: {
    TwoColumnsPageLayout,
    ProgressBar,
    PageSpinner,
    CampaignBox,
    DatePicker,
    'checkout-accident-template': CheckoutAccident,
    'checkout-mobility-template': CheckoutMobility,
    'checkout-car-template': CheckoutCar,
    'checkout-construction-template': CheckoutConstruction,
    'checkout-content-template': CheckoutContent,
    'checkout-health-template': CheckoutHealth,
    'checkout-house-template': CheckoutHouse,
    'checkout-moped-template': CheckoutMoped,
    'checkout-motorcycle-template': CheckoutMotorcycle,
    'checkout-pet-template': CheckoutPet,
    'checkout-vacationhome-template': CheckoutVacationHome,
    'checkout-veteran-template': CheckoutVeteran,
    'checkout-salaryprotection-template': CheckoutSalaryProtection,
    'checkout-caravan-template': CheckoutCaravan,
    'checkout-summary': CheckoutSummary,
    ProposalHeaderView,
    CheckoutProgressBarView,
    BasketPriceList,
    VueSelect
  },
  mixins: [ValidatePropsMixin],
  props: {
    externalViewModel: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      name: 'checkout',
      config: {
        headers: {
          'Content-Type': 'application/json'
        }
      },
      disableNext: false,
      showErrorModal: false,
      showModalLeave: false,
      localCurrentModel: {},
      viewModel: this.externalViewModel
    };
  },
  computed: {
    ...mapState({
      selectedProposals: (state) => state.proposalBasket.selectedProposals,
      // index: (state) => state.proposalBasket.index,
      selectedPaymentIntervals: (state) => state.proposalBasket.selectedPaymentIntervals
    }),
    index: {
      get() {
        return this.$store.state.proposalBasket.index;
      },
      set(newValue) {
        this.$store.commit('proposalBasket/setIndex', newValue);
      }
    },
    filteredViewModel() {
      const extViewModel = cloneDeep(this.viewModel);
      const proposals = extViewModel.Data.Proposals;

      // Filter proposals not selected in basket
      const selectedProposalIds = this.$store.state.proposalBasket.selectedProposals;

      // If no proposals have been selected, do not filter proposals
      if (selectedProposalIds && selectedProposalIds.length > 0) {
        const selectedProposals = proposals.filter((proposal) =>
          selectedProposalIds.includes(proposal.Id)
        );
        const selectedProposalsSortedByLowestId = selectedProposals.sort((a, b) => a.Id - b.Id);
        extViewModel.Data.Proposals = selectedProposalsSortedByLowestId;
      }

      // Add payment intervals chosen in basket
      const selectedPaymentIntervals = this.$store.state.proposalBasket.selectedPaymentIntervals;
      extViewModel.Data.Proposals.forEach((proposal) => {
        const proposalId = proposal.Id;
        const paymentInterval = selectedPaymentIntervals[proposalId];
        if (paymentInterval)
          proposal.PaymentInformation.SelectedPaymentInterval = {
            Id: paymentInterval.id,
            Description: paymentInterval.description
          };
      });

      // Filter claimHistoryDocuments
      const claimHistoryDocuments = extViewModel.Data.ClaimHistoryDocuments;
      const documentIds = [];
      extViewModel.Data.Proposals.forEach((proposal) => {
        if (proposal.Document) documentIds.push(proposal.Document.Id);
      });

      extViewModel.Data.ClaimHistoryDocuments = claimHistoryDocuments.filter((document) =>
        documentIds.includes(document.Id)
      );

      return extViewModel;
    },
    proposalPriceModels() {
      const proposals = this.filteredViewModel.Data.Proposals;
      return proposals.map((proposal) => ({
        name: proposal.Name,
        type: proposal.Type,
        details: proposal.Asset.AssetDetailSingleLine,
        campaignLine: '',
        price: proposal.PriceAsNumber
      }));
    },
    totalProposals() {
      return this.filteredViewModel.Data.Proposals.length;
    },
    isLastProposal() {
      return this.index === this.filteredViewModel.Data.Proposals.length - 1;
    },
    contentModels() {
      let models = Object.getOwnPropertyNames(this.filteredViewModel.Content.ProposalContentModels);
      let modelNames = [];
      models.forEach((model) => {
        modelNames.push(model.toLowerCase());
      });
      return modelNames;
    },
    currentTemplate() {
      if (this.filteredViewModel.Data === undefined) {
        return '';
      }

      if (this.index >= this.totalProposals) {
        return 'checkout-summary';
      }

      switch (this.filteredViewModel.Data.Proposals[this.index].Type) {
        case 'car':
          return 'checkout-car-template';
        case 'construction':
          return 'checkout-construction-template';
        case 'content':
          return 'checkout-content-template';
        case 'health':
          return 'checkout-health-template';
        case 'house':
          return 'checkout-house-template';
        case 'moped':
          return 'checkout-moped-template';
        case 'motorcycle':
          return 'checkout-motorcycle-template';
        case 'veteran':
          return 'checkout-veteran-template';
        case 'personalaccident':
        case 'childaccident':
          return 'checkout-accident-template';
        case 'pet':
          return 'checkout-pet-template';
        case 'vacationhome':
          return 'checkout-vacationhome-template';
        case 'salaryprotection':
          return 'checkout-salaryprotection-template';
        case 'caravan':
          return 'checkout-caravan-template';
        case 'mobility':
          return 'checkout-mobility-template';
      }
      return ''; // TODO: is this the right default?
    },
    currentModel() {
      if (this.index >= this.totalProposals) {
        return this.filteredViewModel.Data;
      }

      return this.filteredViewModel.Data.Proposals[this.index];
    },
    proposalModel() {
      if (this.index >= this.totalProposals) return null;

      let proposal = this.currentModel.Type;
      if (proposal === 'childaccident') proposal = 'personalaccident';

      let index = this.contentModels.findIndex((x) => proposal == x);
      if (index == -1) return null;

      let model = Object.values(this.filteredViewModel.Content.ProposalContentModels)[index];
      return model;
    },
    shouldBeSkipped() {
      if (this.filteredViewModel.Data.Proposals[this.index].Type == 'extendedtravel') {
        return true;
      }
      return false;
    },
    spinnerUrlList() {
      const urlList = [];
      forEach(this.filteredViewModel.Data.Proposals, (proposal) => {
        urlList.push(Utility.constants().API.checkout_save_proposal + proposal.Type + '/save');
      });
      urlList.push(Utility.constants().API.checkout_save_payment);
      urlList.push(Utility.constants().API.proposal_moped_search);
      urlList.push(Utility.constants().API.proposal_caravan_search);
      urlList.push(Utility.constants().API.upload_document_taxation);
      return Array.from(new Set(urlList));
    },
    totalPrice() {
      let totalPrice = 0;
      this.filteredViewModel.Data.Proposals.forEach(
        (proposal) => (totalPrice += proposal.PriceAsNumber)
      );
      return Utility.formatNumberInDKLocale(totalPrice);
    },
    totalFees() {
      let totalFees = 0;
      this.filteredViewModel.Data.Proposals.forEach(
        (proposal) => (totalFees += proposal.FeesAsNumber)
      );
      return Utility.formatNumberInDKLocale(totalFees);
    }
  },
  watch: {
    currentTemplate() {
      if (this.filteredViewModel.Data === undefined) {
        return;
      }

      return this.index === this.filteredViewModel.Data.Proposals.length
        ? this.dispatchEvent(6)
        : false;
    }
  },
  mounted() {
    // Utility.stickyBasket();
    // TODO: uncomment to enable warning when leaving page
    // window.onbeforeunload = (() => {
    //     return 'Vil du forlade siden? Dine indtastninger til supplerende spørgsmål vil gå tabt.'
    // })
    forEach(this.filteredViewModel.Data.Proposals, (proposal) => {
      proposal.Files = [];
    });

    this.dispatchEvent(5);
  },
  beforeCreate() {
    this.$store.dispatch('proposalBasket/setProposals');
  },
  created() {
    if (this.index < this.totalProposals && this.shouldBeSkipped) {
      this.increaseIndex();
    }

    /*this.$on('go-back', () => {
      this.back();
    });

    this.$on('disable-next-button', (val) => {
      this.disableNext = val;
    });*/
  },
  beforeUpdate() {
    if (this.index < this.totalProposals && this.shouldBeSkipped) {
      this.increaseIndex();
    }
  },
  methods: {
    decodeHtml: Utility.decodeHtml,
    next() {
      this.$refs.Form.validate().then((result) => {
        this.keepSessionAlive();

        if (result.valid) {
          this.saveToTheDocument(this.localCurrentModel, () => {
            this.increaseIndex();
          });
        } else {
          this.$refs.proposalView.scrollToFirstError();
        }
      });
    },
    back() {
      this.keepSessionAlive();
      this.decreaseIndex();
      if (this.shouldBeSkipped) {
        this.decreaseIndex();
      }
      this.disableNextButton(false);
    },
    saveToTheDocument(Data, callbackDone) {
      axios
        .post(
          Utility.constants().API.checkout_save_proposal + Data.Type + '/save',
          JSON.stringify(Data),
          this.config
        )
        .then(() => {
          callbackDone(this);
        })
        .catch(() => {});
    },
    keepSessionAlive() {
      axios.get(Utility.constants().API.keep_alive_session);
    },
    disableNextButton(val) {
      this.disableNext = val;
    },
    dispatchEvent(checkoutStep) {
      const proposals = this.filteredViewModel.Data.Proposals;
      let productsArray = [];
      proposals.forEach((proposal) => {
        productsArray.push({
          name: proposal.Name,
          id: proposal.ProductType,
          price: Utility.getGoogleFriendlyPrice(proposal.Price),
          brand: Utility.getBrandIdentifier(),
          channelTypeId: proposal.ChannelTypeId,
          channelTypeName: proposal.ChannelTypeName,
          quantity: 1
        });
      });
      this.$store.dispatch('Events/register', {
        event: 'checkout-steps',
        id: 'ecommerce',
        vm: {
          actionField: {
            step: checkoutStep
          },
          products: productsArray
        }
      });
    },
    updateCurrentModel(param) {
      this.localCurrentModel = param;
    },
    increaseIndex() {
      this.index++;
    },
    decreaseIndex() {
      this.index--;
    }
  }
};
</script>
<style lang="scss" scoped>
@import 'src/styles/all.scss';

.checkout {
  &__campaign-box--desktop {
    @include breakpoint(large up) {
      margin-top: rem-calc(48);
    }
  }
}
</style>
